import { API_URL } from "../config";
import { logger } from "./axiom";

class ApiClient {
    private baseUrl: string;

    private token: string | null = null;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
        if (typeof localStorage !== "undefined") {
            const token = localStorage.getItem("token");
            if (token) {
                this.token = token;
            }
        }
    }

    setToken(token: string) {
        this.token = token;
    }

    getToken(): string | null {
        return this.token;
    }

    private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        const url = `${this.baseUrl}${endpoint}`;
        const method = (options.method || "GET").toUpperCase();
        const headers = new Headers(options.headers);

        if (this.token) {
            headers.set("Authorization", `Bearer ${this.token}`);
        }

        // Default to JSON content type for bodies
        if (!headers.has("Content-Type") && !(options.body instanceof FormData)) {
            headers.set("Content-Type", "application/json");
        }

        const config: RequestInit = {
            ...options,
            headers,
            mode: "cors",
        };

        try {
            const response = await fetch(url, config);

            if (!response.ok) {
                logger.warn("API request failed", {
                    endpoint,
                    method,
                    status: response.status,
                    statusText: response.statusText,
                });
                void logger.flush();
                throw new Error(`API Error: ${response.status} ${response.statusText}`);
            }

            // Handle 204 No Content
            if (response.status === 204) {
                return {} as T;
            }

            return response.json();
        } catch (error) {
            if (!(error instanceof Error && error.message.startsWith("API Error:"))) {
                logger.error("API request error", {
                    endpoint,
                    method,
                    message: error instanceof Error ? error.message : String(error),
                });
                void logger.flush();
            }
            throw error;
        }
    }

    get<T>(endpoint: string, options?: RequestInit) {
        return this.request<T>(endpoint, { ...options, method: "GET" });
    }

    post<T>(endpoint: string, body: any, options?: RequestInit) {
        return this.request<T>(endpoint, {
            ...options,
            method: "POST",
            body: JSON.stringify(body),
        });
    }

    put<T>(endpoint: string, body: any, options?: RequestInit) {
        return this.request<T>(endpoint, {
            ...options,
            method: "PUT",
            body: JSON.stringify(body),
        });
    }

    putBinary<T>(endpoint: string, body: Blob | ArrayBuffer | string, options?: RequestInit) {
        const headers = new Headers(options?.headers);
        // Explicitly set or let browser handle Content-Type for binary
        if (!headers.has("Content-Type")) {
            // If it's a File/Blob, we might want to preserve its type
            if (body instanceof Blob && body.type) {
                headers.set("Content-Type", body.type);
            } else {
                headers.set("Content-Type", "application/octet-stream");
            }
        }

        return this.request<T>(endpoint, {
            ...options,
            method: "PUT",
            body,
            headers,
            mode: "cors",
        });
    }

    delete<T>(endpoint: string, options?: RequestInit) {
        return this.request<T>(endpoint, { ...options, method: "DELETE" });
    }
}

export const api = new ApiClient(API_URL);
